/* eslint-disable no-restricted-imports */
import React, {useMemo, useState} from 'react'
import styles from './DisplayTabContent.module.scss'
import clsx from 'clsx'
import {UIUtilsEjsVariables} from '../../../../../../code/client/shared/scripts/UIUtilsEjsVariables'
import {useUIGameSettingsContext} from '../../../contexts/UIGameSettingsContext'
import {StorageWrapper} from '../../../utils/storage'
import {TranslationObject} from '@colonist/i18n'
import {T} from '../../../common/T'

const DisplayInputField = ({
    label,
    value,
    options,
    onChange,
}: {
    label: TranslationObject
    value: string | number | boolean
    options: {
        value: string | number | boolean
        label: TranslationObject
    }[]
    onChange: (value: string | number | boolean) => void
}) => {
    const selectedIndex = useMemo(() => {
        const idx = options.findIndex((option) => option.value === value)
        return idx >= 0 ? idx : 0
    }, [options, value])

    const selectedValue = useMemo(() => options[selectedIndex], [options, selectedIndex])

    const handleBackClick = () => {
        if(selectedIndex > 0) {
            onChange(options[selectedIndex - 1].value)
        }
    }

    const handleNextClick = () => {
        if(selectedIndex < options.length - 1) {
            onChange(options[selectedIndex + 1].value)
        }
    }

    return (
        <div className={styles.displayInputField}>
            <div className={styles.label}><T id={label} /></div>
            <div className={styles.fieldWrapper}>
                <img
                    alt="Back"
                    className={clsx(styles.backButton, {[styles.disabled]: selectedIndex === 0})}
                    onClick={handleBackClick}
                    src="/dist/images/icon_arrow.svg"
                />
                <div className={styles.fieldValue}><T id={selectedValue.label} /></div>
                <img
                    alt="Next"
                    className={clsx(styles.nextButton, {[styles.disabled]: selectedIndex === options.length - 1})}
                    onClick={handleNextClick}
                    src="/dist/images/icon_arrow.svg"
                />
            </div>
        </div>
    )
}

export const DisplayTabContent = () => {
    const {updateCanvasSettings} = useUIGameSettingsContext()
    const [animationStatus, setAnimationStatus] = useState<boolean>(StorageWrapper.getAnimation() ?? true)
    const [keyboardShortcuts, setKeyBoardShortcuts] = useState<boolean>(StorageWrapper.getKeyboardShortcuts() ?? true)
    const defaultAntialias = window.devicePixelRatio * window.innerWidth <= 2000
    const [antialias, setAntialias] = useState<boolean>(StorageWrapper.getAntialias() ?? defaultAntialias)

    const defaultResolution = useMemo(() => {
        const allowedResolutions = [0.5, 1, 1.5, 2]
        const rawResolution = UIUtilsEjsVariables.isUserOnDiscord ? 2 : window.devicePixelRatio
        return allowedResolutions.reduce((prev, curr) => curr <= rawResolution ? curr : prev, 0.5)
    }, [])

    const [resolution, setResolution] = useState<number>(StorageWrapper.getResolution() ?? defaultResolution)
    const [powerPreference, setPowerPreference] = useState<StorageWrapper.PowerPreference>(StorageWrapper.getPowerPreference())
    const handleChangeAnimationStatus = (value: boolean) => {
        StorageWrapper.setAnimation(value)
        setAnimationStatus(value)
    }

    const handleChangeKeyboardShortcuts = (value: boolean) => {
        StorageWrapper.setKeyboardShortcuts(value)
        setKeyBoardShortcuts(value)
    }

    const handleChangeAntialias = (value: boolean) => {
        updateCanvasSettings({antialias: value})
        StorageWrapper.setAntialias(value)
        setAntialias(value)
    }

    const handleChangeResolution = (value: number) => {
        updateCanvasSettings({resolution: value})
        StorageWrapper.setResolution(value)
        setResolution(value)
    }

    const handleChangePowerPreference = (value: StorageWrapper.PowerPreference) => {
        updateCanvasSettings({powerPreference: value})
        StorageWrapper.setPowerPreference(value)
        setPowerPreference(value)
    }

    return (
        <div className={styles.container}>
            <DisplayInputField
                label={{key: 'strings:game.settings.displayTab.animation.title'}}
                onChange={handleChangeAnimationStatus}
                options={[
                    {value: false, label: {key: 'strings:game.settings.displayTab.animation.options.Inactive'}},
                    {value: true, label: {key: 'strings:game.settings.displayTab.animation.options.Active'}},
                ]}
                value={animationStatus}
            />

            <DisplayInputField
                label={{key: 'strings:game.settings.displayTab.keyboardShortcuts.title'}}
                onChange={handleChangeKeyboardShortcuts}
                options={[
                    {value: false, label: {key: 'strings:game.settings.displayTab.keyboardShortcuts.options.Off'}},
                    {value: true, label: {key: 'strings:game.settings.displayTab.keyboardShortcuts.options.On'}},
                ]}
                value={keyboardShortcuts}
            />

            <DisplayInputField
                label={{key: 'strings:game.settings.displayTab.antialias.title'}}
                onChange={handleChangeAntialias}
                options={[
                    {value: false, label: {key: 'strings:game.settings.displayTab.antialias.options.Inactive'}},
                    {value: true, label: {key: 'strings:game.settings.displayTab.antialias.options.Active'}},
                ]}
                value={antialias}
            />

            <DisplayInputField
                label={{key: 'strings:game.settings.displayTab.resolution.title'}}
                onChange={handleChangeResolution}
                options={[
                    {value: 0.5, label: {key: 'strings:colonistCommon.utils.#buffer', options: {value: 0.5}}},
                    {value: 1, label: {key: 'strings:colonistCommon.utils.#buffer', options: {value: 1}}},
                    {value: 1.5, label: {key: 'strings:colonistCommon.utils.#buffer', options: {value: 1.5}}},
                    {value: 2, label: {key: 'strings:colonistCommon.utils.#buffer', options: {value: 2}}},
                ]}
                value={resolution}
            />

            <DisplayInputField
                label={{key: 'strings:game.settings.displayTab.power.title'}}
                onChange={handleChangePowerPreference}
                options={[
                    {
                        value: StorageWrapper.PowerPreference.Low,
                        label: {key: 'strings:game.settings.displayTab.power.options.LowPower'}},
                    {
                        value: StorageWrapper.PowerPreference.Default,
                        label: {key: 'strings:game.settings.displayTab.power.options.Default'}},
                    {
                        value: StorageWrapper.PowerPreference.High, label:
                        {key: 'strings:game.settings.displayTab.power.options.HighPerformance'}},
                ]}
                value={powerPreference}
            />
        </div>
    )
}
